// Pure bar-scale math for the Objectives table's mortality bar cell. Kept outside
// the .vue files so the scale rule is unit-testable in isolation the day a runner lands, same
// reasoning as objectiveRow.ts/deviation.ts.
//
// One shared ceiling for every bar in the table (Barn and Section rows alike) so bar length is
// directly comparable row-to-row, snapped to a "nice" number derived from the largest resolved
// target currently on screen — adapts to Weaner vs SlaughterPig target ranges without a hardcoded
// constant to re-tune later. A row whose own value exceeds the ceiling fills to 100% and is flagged
// via isBarOverflow rather than stretching everyone else's scale to fit one outlier.

// "Nice" ceiling candidates for the bar scale.
const CEILING_STEPS = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 50];

// Smallest clean step that fits the largest on-screen target comfortably inside it (target × ~4).
// Falls back to the smallest step when nothing on screen has a target at all — there's no bar to draw
// in that case (see objectiveRow.ts's no-target rows staying plain text), but the function must still
// return a positive number.
export function computeSharedCeiling(targets: Array<number | null>): number {
    const maxTarget = targets.reduce<number>((max, target) => (target != null && target > max ? target : max), 0);
    if (maxTarget <= 0) return CEILING_STEPS[0];
    return CEILING_STEPS.find((step) => step >= maxTarget * 4) ?? maxTarget * 4;
}

export function barFillPercent(currentValue: number, ceiling: number): number {
    if (ceiling <= 0) return 0;
    return Math.min(100, (currentValue / ceiling) * 100);
}

export function barTickPercent(target: number, ceiling: number): number {
    if (ceiling <= 0) return 0;
    return Math.min(100, (target / ceiling) * 100);
}

export function isBarOverflow(currentValue: number, ceiling: number): boolean {
    return currentValue > ceiling;
}
